import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Shield, Globe2, Clock, X, Loader2, ShieldCheck, ShieldAlert, BellOff, Bell, LogOut } from "lucide-react";

/**
 * User-facing security panel. Paired with the new-country sign-in alerts
 * system. Lets the user:
 *   • See + revoke individual trusted countries (`known_login_countries`)
 *   • Review their last 10 sign-ins (from `user_login_history`)
 *   • Toggle `security_alerts_muted` (stop receiving the "new country" emails)
 *
 * No secrets are exposed — we only show country, IP, and timestamp, all of
 * which the user already receives in the alert email itself.
 */
export default function SecuritySection() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [revoking, setRevoking] = useState("");
  const [signingOut, setSigningOut] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/me/security");
      setState(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load security state");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggleMute = async (next) => {
    setSaving(true);
    try {
      await api.put("/me/security/muted", { muted: next });
      setState((s) => ({ ...s, security_alerts_muted: next }));
      toast.success(next ? "New-country alerts muted" : "New-country alerts enabled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not update preference");
    } finally {
      setSaving(false);
    }
  };

  const revokeCountry = async (code) => {
    setRevoking(code);
    try {
      await api.delete(`/me/security/countries/${code}`);
      setState((s) => ({
        ...s,
        known_login_countries: (s.known_login_countries || []).filter((c) => c !== code),
      }));
      toast.success(`Removed ${code} from trusted countries`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not revoke country");
    } finally {
      setRevoking("");
    }
  };

  const signOutOthers = async () => {
    const ok = window.confirm(
      "Sign out all OTHER sessions on all devices?\n\nThis keeps you signed in here but kicks every other browser / device. Use this if you suspect unauthorized access."
    );
    if (!ok) return;
    setSigningOut(true);
    try {
      const { data } = await api.post("/me/security/sign-out-other-sessions");
      // Swap the stored token so this session survives the mass-revoke.
      if (data.token) localStorage.setItem("rv_token", data.token);
      toast.success("All other sessions signed out. Only this device remains signed in.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not sign out other sessions");
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <section
      className="bg-white border border-slate-200 rounded-sm p-6 mb-6"
      data-testid="security-section"
    >
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 bg-slate-900 text-white flex items-center justify-center rounded-sm">
          <Shield className="w-4 h-4" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Security
          </h2>
          <p className="text-sm text-slate-500">
            Review trusted countries, recent sign-ins, and control new-country alert emails.
          </p>
        </div>
      </div>

      {loading || !state ? (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <div className="space-y-6">
          {/* Alerts preference */}
          <div
            className="flex items-start justify-between gap-4 border border-slate-200 rounded-sm p-4"
            data-testid="security-alerts-toggle"
          >
            <div className="flex items-start gap-3 min-w-0">
              {state.security_alerts_muted ? (
                <BellOff className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              ) : (
                <Bell className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
              )}
              <div className="min-w-0">
                <div className="font-semibold text-sm text-slate-900">
                  New-country sign-in alerts
                </div>
                <div className="text-xs text-slate-500 leading-relaxed mt-0.5">
                  Email me when someone signs in from a country I haven't signed in from before.
                  Mute if you travel frequently or use a VPN across regions.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => toggleMute(!state.security_alerts_muted)}
              disabled={saving}
              data-testid="security-mute-toggle"
              className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-sm border transition-colors ${
                state.security_alerts_muted
                  ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
                  : "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
              }`}
            >
              {saving ? "Saving…" : state.security_alerts_muted ? "Muted — enable" : "Enabled — mute"}
            </button>
          </div>

          {/* Trusted countries */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-emerald-700" />
              <div className="overline text-slate-600">Trusted countries</div>
            </div>
            {(state.known_login_countries || []).length === 0 ? (
              <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="security-trusted-empty">
                No countries trusted yet. Your first sign-in from each country will trigger an alert — tap "Yes, it was me" to trust it going forward.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="security-trusted-list">
                {state.known_login_countries.map((code) => (
                  <span
                    key={code}
                    data-testid={`security-trusted-${code}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs font-mono"
                  >
                    <Globe2 className="w-3 h-3" /> {code}
                    <button
                      type="button"
                      onClick={() => revokeCountry(code)}
                      disabled={revoking === code}
                      className="ml-1 text-emerald-700 hover:text-red-700 hover:bg-red-50 rounded-sm w-4 h-4 flex items-center justify-center transition-colors"
                      data-testid={`security-revoke-${code}`}
                      title={`Revoke trust for ${code}`}
                    >
                      {revoking === code ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                    </button>
                  </span>
                ))}
              </div>
            )}
            <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
              Revoking a country means your next sign-in from there will trigger a fresh alert email.
            </p>
          </div>

          {/* Recent sign-ins */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-slate-600" />
              <div className="overline text-slate-600">Recent sign-ins</div>
            </div>
            {(state.recent_logins || []).length === 0 ? (
              <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3" data-testid="security-recent-empty">
                No sign-ins recorded yet.
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-sm">
                <table className="w-full text-sm" data-testid="security-recent-table">
                  <thead className="bg-slate-50 border-b border-slate-200 text-left">
                    <tr>
                      <th className="px-4 py-2 overline text-slate-600">When</th>
                      <th className="px-4 py-2 overline text-slate-600">Country</th>
                      <th className="px-4 py-2 overline text-slate-600">IP address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {state.recent_logins.map((r, idx) => (
                      <tr key={idx} className="border-b border-slate-100 last:border-0" data-testid={`security-recent-row-${idx}`}>
                        <td className="px-4 py-2 text-slate-800 whitespace-nowrap">
                          {r.at ? new Date(r.at).toLocaleString() : "—"}
                        </td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-700">{r.country || "—"}</td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-500">{r.ip || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {state.last_login_ip && (
              <div className="flex items-center gap-2 mt-3 text-xs text-slate-500" data-testid="security-last-ip">
                <ShieldAlert className="w-3.5 h-3.5 text-slate-400" />
                Most recent sign-in IP: <span className="font-mono">{state.last_login_ip}</span>
              </div>
            )}
          </div>

          {/* Sign out all other sessions — last-resort control if the user's
              account may be compromised. Only kicks OTHER sessions; this one survives. */}
          <div
            className="flex items-start justify-between gap-4 border border-slate-200 rounded-sm p-4 bg-slate-50"
            data-testid="security-signout-others-block"
          >
            <div className="flex items-start gap-3 min-w-0">
              <LogOut className="w-5 h-5 text-red-700 mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-sm text-slate-900">
                  Sign out all other sessions
                </div>
                <div className="text-xs text-slate-500 leading-relaxed mt-0.5">
                  Invalidate every active sign-in on other browsers and devices. This session stays signed in. Use this after denying a "wasn't me" alert.
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={signOutOthers}
              disabled={signingOut}
              data-testid="security-signout-others"
              className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-sm border border-red-300 bg-red-50 text-red-800 hover:bg-red-100 disabled:opacity-60"
            >
              {signingOut ? "Signing out…" : "Sign out others"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
